/**
 * The one sink the review workbench's telemetry is handed to.
 *
 * `useReviewTelemetry` ships with NO transport installed — deliberately, so that before this endpoint
 * existed a reviewer's browser never emitted 404s. This module is the other half. It is installed once,
 * at bootstrap, by `installReviewTelemetryTransport()`; nothing at a call site changes when it lands.
 *
 * ── what it does with an envelope ────────────────────────────────────────────────────────────────
 * Buffers it. The endpoint takes an ARRAY (`{ events: [...] }`, up to 50) and is throttled at 120
 * requests a minute; a reviewer at the rubric's pace emits roughly nine events per item, so one POST per
 * event would be wasteful and one POST per fifty is comfortable. A batch is flushed when it fills, when
 * the flush interval elapses, or when the page is being hidden — `pagehide` and `visibilitychange`, the
 * events that actually fire on a tab close, a refresh, a crash and a closed lid, none of which run a Vue
 * unmount hook.
 *
 * ── why `fetch(keepalive)` and not `sendBeacon` ──────────────────────────────────────────────────
 * The API is bearer-authenticated. `navigator.sendBeacon` cannot set an Authorization header, so a
 * beacon arrives unauthenticated and is refused — it would LOOK like a release and release nothing.
 * `releaseBeacon()` in ReviewQueuePage.vue reached the same conclusion for the claim release and this
 * copies its shape exactly.
 *
 * ── the rule that overrides everything else ──────────────────────────────────────────────────────
 * Telemetry that can break the tool it measures is worse than none. Nothing in here throws to a caller.
 * A failed POST is a network fact the composable counts as `dropped`; it is not retried, because a retry
 * loop inside a reviewer's tab is a second way to hurt the flow. The promise returned per envelope
 * settles with the batch it rode in, so the composable's `.catch` can observe a rejection without the
 * page ever seeing it.
 */

import { setReviewTelemetryTransport } from '@/composables/useReviewTelemetry'

export const BATCH_SIZE = 50
export const FLUSH_MS = 5000
export const ENDPOINT = '/ops/review-telemetry'
const TOKEN_KEY = 'moroorato_admin_token'

function baseUrl() {
  return process.env.VUE_APP_API_URL || '/api/v1/admin'
}

/**
 * Build a transport bound to a `fetch` and a `window` (injectable for tests). Returns the function
 * `setReviewTelemetryTransport()` expects: `(envelope) => Promise`.
 */
export function createReviewTelemetryTransport({ fetchFn, win, batchSize = BATCH_SIZE, flushMs = FLUSH_MS } = {}) {
  const w = win || (typeof window !== 'undefined' ? window : null)
  const doFetch = fetchFn || (w && typeof w.fetch === 'function' ? w.fetch.bind(w) : null)

  let buffer = []
  let waiters = [] // one { resolve, reject } per buffered envelope
  let timer = null

  const settle = (fn) => {
    const pending = waiters
    waiters = []
    for (const p of pending) { try { fn(p) } catch (_e) { /* a waiter must never throw into us */ } }
  }

  function flush({ keepalive = false } = {}) {
    if (timer) { w && w.clearTimeout(timer); timer = null }
    if (buffer.length === 0) return Promise.resolve()

    const events = buffer
    buffer = []

    if (!doFetch) {
      settle((p) => p.reject(new Error('no fetch available')))
      return Promise.resolve()
    }

    const token = w && w.localStorage ? w.localStorage.getItem(TOKEN_KEY) : null
    let request
    try {
      request = doFetch(`${baseUrl()}${ENDPOINT}`, {
        method: 'POST',
        keepalive,
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
          ...(token ? { Authorization: `Bearer ${token}` } : {}),
        },
        body: JSON.stringify({ events }),
      })
    } catch (e) {
      settle((p) => p.reject(e))
      return Promise.resolve()
    }

    return Promise.resolve(request).then(
      (res) => {
        if (res && res.ok === false) settle((p) => p.reject(new Error(`review-telemetry ${res.status}`)))
        else settle((p) => p.resolve())
      },
      (e) => settle((p) => p.reject(e)),
    )
  }

  function transport(envelope) {
    const promise = new Promise((resolve, reject) => waiters.push({ resolve, reject }))
    // The composable attaches its own .catch; this one stops an unobserved rejection in the interval
    // between buffering and that attachment from surfacing as "unhandled".
    promise.catch(() => {})
    buffer.push(envelope)

    if (buffer.length >= batchSize) flush()
    else if (!timer && w) timer = w.setTimeout(() => flush(), flushMs)

    return promise
  }

  transport.flush = flush

  if (w && typeof w.addEventListener === 'function') {
    const onHide = () => flush({ keepalive: true })
    w.addEventListener('pagehide', onHide)
    w.addEventListener('visibilitychange', () => {
      if (w.document && w.document.visibilityState === 'hidden') onHide()
    })
  }

  return transport
}

/** Install at bootstrap. Idempotent by construction — the seam holds one function. */
export function installReviewTelemetryTransport(options) {
  const transport = createReviewTelemetryTransport(options)
  setReviewTelemetryTransport(transport)
  return transport
}
